<template>
  <vetster-slide-over
    ref="petFormSlideOver"
    cancel-label=""
    :container-classes="containerClasses"
    full-screen
    hide-header
    :show-overlay="!isTopSlideOver"
    size="lg"
  >
    <template #header>
      <vetster-slide-over-common-heading
        :badge="badge.label"
        :badge-classes="badgeClasses"
        :badge-variant="badge.variant"
        :header="heading"
        :show-back="isTopSlideOver"
        :show-close="!isTopSlideOver"
        :sub-header="subHeader"
        class="px-4 space-y-5"
        @back="back"
        @close="back"
      />
    </template>
    <template #content>
      <component
        :is="formComponent"
        :ref="`${form}Form`"
        :form-inputs="formInputs"
        @input="updateInputs"
      />
    </template>
    <template #footer>
      <div
        data-toast-anchor
        class="p-4 sm:flex sm:justify-end"
      >
        <vetster-button
          variant="ocean"
          class="w-full sm:w-1/2 rounded-full"
          @click="save"
        >
          {{ $t('app.save') }}
        </vetster-button>
      </div>
    </template>
  </vetster-slide-over>
</template>

<script>
import VetsterBirthDateForm from '@/brand-components/VetsterBirthDateForm';
import VetsterBreedForm from '@/brand-components/VetsterBreedForm';
import VetsterGenderForm from '@/brand-components/VetsterGenderForm';
import VetsterSlideOver from '@/brand-components/VetsterSlideOver';
import VetsterSlideOverCommonHeading from '@/brand-components/VetsterSlideOverCommonHeading';
import VetsterSpeciesForm from '@/brand-components/VetsterSpeciesForm';
import VetsterWeightForm from '@/brand-components/VetsterWeightForm';
import PetDetailsForm from '@/enums/PetDetailsForm';

export default {
  name: 'VetsterPetFormSlideOver',

  components: {
    VetsterBirthDateForm,
    VetsterBreedForm,
    VetsterGenderForm,
    VetsterSlideOver,
    VetsterSlideOverCommonHeading,
    VetsterSpeciesForm,
    VetsterWeightForm,
  },

  props: {
    containerClasses: {
      type: String,
      default: 'lg:h-[80vh]',
    },
    form: {
      type: String,
      default: '',
    },
    inputs: {
      type: Object,
      default: () => ({}),
    },
    isTopSlideOver: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      badgeClasses: '',
      currentForm: null,
      formInputs: {},
    };
  },

  computed: {
    badge() {
      if (this.form === PetDetailsForm.Species) {
        return '';
      }

      if (this.form === PetDetailsForm.Sex && !this.formInputs.gender) {
        return {
          label: this.$t('app.required'),
          variant: 'red',
        };
      }

      return {
        label: '',
        variant: '',
      };
    },
    formComponent() {
      switch (this.form) {
        case PetDetailsForm.Species:
          return VetsterSpeciesForm;
        case PetDetailsForm.Breed:
          return VetsterBreedForm;
        case PetDetailsForm.Sex:
          return VetsterGenderForm;
        case PetDetailsForm.BirthDate:
          return VetsterBirthDateForm;
        case PetDetailsForm.Weight:
          return VetsterWeightForm;
        default:
          return VetsterSpeciesForm;
      }
    },
    heading() {
      switch (this.form) {
        case PetDetailsForm.Species:
          return this.$t('search.choose-pet-species');
        case PetDetailsForm.Breed:
          return this.$t('search.what-breed-is-your-pet');
        case PetDetailsForm.Sex:
          return this.$t('search.what-sex-is-your-pet');
        case PetDetailsForm.BirthDate:
          return this.$t('search.when-is-your-pets-birth-date');
        case PetDetailsForm.Weight:
          return this.$t('search.how-much-does-pet-weigh');
        default:
          return this.$t('search.choose-pet-species');
      }
    },
    isBirthDateMissing() {
      return !this.formInputs.birthday_on && this.form === PetDetailsForm.BirthDate;
    },
    isReproductiveStatusMissing() {
      return typeof this.formInputs.spayed_neutered !== 'boolean' && this.form === PetDetailsForm.Sex;
    },
    isSexMissing() {
      return !this.formInputs.gender && this.form === PetDetailsForm.Sex;
    },
    isSpeciesMissing() {
      return !this.formInputs.species && this.form === PetDetailsForm.Species;
    },
    isWeightMissing() {
      return !this.formInputs.weight && this.form === PetDetailsForm.Weight;
    },
    missingFieldMessage() {
      if (this.isSpeciesMissing) {
        return this.$t('search.please-select-pets-species-to-proceed');
      }

      if (this.isSexMissing) {
        return this.$t('search.please-select-pets-sex-to-proceed');
      }

      if (this.isReproductiveStatusMissing) {
        return this.$t('search.please-select-pets-reproductive-status-to-proceed');
      }

      if (this.isBirthDateMissing) {
        return this.$t('search.please-select-pets-birth-date-to-proceed');
      }

      if (this.isWeightMissing) {
        return this.$t('search.please-select-pets-weight-to-proceed');
      }

      return '';
    },
    subHeader() {
      switch (this.form) {
        default:
          return '';
      }
    },
  },

  methods: {
    back() {
      this.$refs.petFormSlideOver.close();
      this.$emit('close');
    },

    open() {
      this.formInputs = { ...this.inputs };
      this.$refs.petFormSlideOver.open();
    },

    save() {
      if (this.validateInputs()) {
        this.$emit('updateInputs', this.formInputs);
        this.back();
      }
    },

    updateInputs(updatedInputs) {
      this.formInputs = updatedInputs;
    },

    validateInputs() {
      if (
        this.isSpeciesMissing
        || this.isSexMissing
        || this.isReproductiveStatusMissing
        || this.isBirthDateMissing
      ) {
        this.badgeClasses = 'animated shake fast';

        setTimeout(() => {
          this.badgeClasses = '';
        }, 800);

        if (this.missingFieldMessage) {
          this.$toast(this.missingFieldMessage, {
            status: 'error',
            timeout: 8000,
          });
        }

        return false;
      }

      const currentForm = this.$refs[`${this.form}Form`];

      if (currentForm?.v$ && !currentForm.hasValidInputs(currentForm.v$.inputs, currentForm)) {
        if (this.form === PetDetailsForm.Breed) {
          currentForm.focusBreed?.();
        }

        return false;
      }

      return true;
    },
  },
};
</script>
